import React, { useEffect, useMemo, useState } from 'react'
import { Search, Send, X } from 'lucide-react'
import { api, getApiErrorMessage } from '../lib/api'
import { unwrapAsyncResult } from '../lib/asyncOperation'
import { useToast } from '../ui/ToastProvider'

const TARGET_SELECTED = 'SELECTED_USERS'
const TARGET_ACTIVE_PUSH = 'ACTIVE_PUSH_USERS'
const PURPOSE_SERVICE_NOTICE = 'SERVICE_NOTICE'
const PURPOSE_MARKETING = 'MARKETING'

function unwrap(res, fallback) {
  return (res?.data && res.data.data !== undefined) ? res.data.data : (res?.data ?? fallback)
}

function resultLabel(result) {
  if (!result) return '-'
  if (result.disabled) return 'FCM 비활성'
  const ok = Number(result.successCount || 0)
  const fail = Number(result.failureCount || 0)
  return `성공 ${ok.toLocaleString()} / 실패 ${fail.toLocaleString()}`
}

function alimtalkResultLabel(result) {
  if (!result) return '-'
  const attempted = Number(result.attemptedCount || 0)
  const ok = Number(result.successCount || 0)
  const fail = Number(result.failureCount || 0)
  return `시도 ${attempted.toLocaleString()} · 성공 ${ok.toLocaleString()} / 실패 ${fail.toLocaleString()}`
}

function orgLabel(org) {
  return org?.displayName || org?.name || org?.orgName || '조직'
}

function parseVariableField(raw) {
  if (!raw) return []
  let parsed = raw
  if (typeof raw === 'string') {
    try {
      parsed = JSON.parse(raw)
    } catch (_) {
      return []
    }
  }

  const names = new Set()
  const add = (value) => {
    const name = String(value || '')
      .replace(/^#\{/, '')
      .replace(/^\$\{/, '')
      .replace(/\}$/, '')
      .trim()
    if (name) names.add(name)
  }

  if (Array.isArray(parsed)) {
    parsed.forEach((item) => {
      if (typeof item === 'string') {
        add(item)
      } else if (item && typeof item === 'object') {
        add(item.name || item.key || item.id || item.code)
      }
    })
  } else if (parsed && typeof parsed === 'object') {
    if (parsed.properties && typeof parsed.properties === 'object') {
      Object.keys(parsed.properties).forEach(add)
    } else {
      Object.keys(parsed).forEach(add)
    }
  }
  return Array.from(names)
}

function templateVariableNames(template) {
  if (!template) return []
  const names = new Set()
  parseVariableField(template.requiredVariables).forEach((name) => names.add(name))
  parseVariableField(template.optionalVariables).forEach((name) => names.add(name))
  parseVariableField(template.variableSchema).forEach((name) => names.add(name))
  return Array.from(names)
}

function variableLabel(name) {
  const labels = {
    title: '제목',
    subject: '제목',
    message: '내용',
    content: '내용',
    summary: '요약',
    link: '링크',
    url: 'URL',
    summaryUrl: '상세 링크',
  }
  return labels[name] || name
}

function canReceiveKakao(item) {
  return item?.canReceiveKakao === true
}

export default function OperatorPushNotificationsPage() {
  const { show } = useToast()
  const [targetMode, setTargetMode] = useState(TARGET_SELECTED)
  const [webEnabled, setWebEnabled] = useState(true)
  const [appEnabled, setAppEnabled] = useState(true)
  const [alimtalkEnabled, setAlimtalkEnabled] = useState(false)
  const [confirmed, setConfirmed] = useState(false)
  const [messagePurpose, setMessagePurpose] = useState(PURPOSE_SERVICE_NOTICE)
  const [title, setTitle] = useState('')
  const [body, setBody] = useState('')
  const [query, setQuery] = useState('')
  const [candidates, setCandidates] = useState([])
  const [selected, setSelected] = useState([])
  const [orgOptions, setOrgOptions] = useState([])
  const [orgQuery, setOrgQuery] = useState('')
  const [selectedOrgId, setSelectedOrgId] = useState('')
  const [templates, setTemplates] = useState([])
  const [templatesLoading, setTemplatesLoading] = useState(false)
  const [selectedTemplateCode, setSelectedTemplateCode] = useState('')
  const [alimtalkVariables, setAlimtalkVariables] = useState({})
  const [loading, setLoading] = useState(false)
  const [orgSelecting, setOrgSelecting] = useState(false)
  const [orgsLoading, setOrgsLoading] = useState(false)
  const [sending, setSending] = useState(false)
  const [result, setResult] = useState(null)

  useEffect(() => {
    let cancelled = false
    async function loadOrgs() {
      setOrgsLoading(true)
      try {
        const res = await api.get('/orgs/options', { params: { scope: 'all' }, __silent: true })
        const payload = unwrap(res, [])
        const rows = Array.isArray(payload) ? payload : []
        const sorted = [...rows].sort((a, b) => String(orgLabel(a)).localeCompare(String(orgLabel(b)), 'ko'))
        if (!cancelled) setOrgOptions(sorted)
      } catch (_) {
        if (!cancelled) setOrgOptions([])
      } finally {
        if (!cancelled) setOrgsLoading(false)
      }
    }
    loadOrgs()
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    let cancelled = false
    async function loadTemplates() {
      setTemplatesLoading(true)
      try {
        const res = await api.get('/operator/alimtalk-sends/templates', { __silent: true })
        const payload = unwrap(res, [])
        const rows = Array.isArray(payload) ? payload : []
        if (!cancelled) {
          setTemplates(rows)
          setSelectedTemplateCode((prev) => (
            rows.some((item) => item.templateCode === prev) ? prev : (rows[0]?.templateCode || '')
          ))
        }
      } catch (_) {
        if (!cancelled) {
          setTemplates([])
          setSelectedTemplateCode('')
        }
      } finally {
        if (!cancelled) setTemplatesLoading(false)
      }
    }
    loadTemplates()
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    let cancelled = false
    const timer = setTimeout(async () => {
      setLoading(true)
      try {
        const params = {}
        if (query.trim()) params.q = query.trim()
        if (selectedOrgId) params.orgId = Number(selectedOrgId)
        const res = await api.get('/operator/push-notifications/recipients', { params, __silent: true })
        const payload = unwrap(res, [])
        if (!cancelled) setCandidates(Array.isArray(payload) ? payload : [])
      } catch (_) {
        if (!cancelled) setCandidates([])
      } finally {
        if (!cancelled) setLoading(false)
      }
    }, 250)
    return () => {
      cancelled = true
      clearTimeout(timer)
    }
  }, [query, selectedOrgId])

  const selectedIds = useMemo(() => new Set(selected.map((item) => item.userId)), [selected])
  const availableCandidates = useMemo(
    () => candidates.filter((item) => !selectedIds.has(item.userId)),
    [candidates, selectedIds],
  )
  const selectedOrg = useMemo(
    () => orgOptions.find((org) => String(org.id) === String(selectedOrgId)) || null,
    [orgOptions, selectedOrgId],
  )
  const filteredOrgOptions = useMemo(() => {
    const q = orgQuery.trim().toLowerCase()
    const base = Array.isArray(orgOptions) ? orgOptions : []
    const filtered = q ? base.filter((org) => String(orgLabel(org)).toLowerCase().includes(q)) : base
    const limited = filtered.slice(0, 120)
    const current = selectedOrgId ? base.find((org) => String(org.id) === String(selectedOrgId)) : null
    if (current && !limited.some((org) => String(org.id) === String(current.id))) {
      return [current, ...limited]
    }
    return limited
  }, [orgOptions, orgQuery, selectedOrgId])
  const selectedTemplate = useMemo(
    () => templates.find((item) => item.templateCode === selectedTemplateCode) || null,
    [templates, selectedTemplateCode],
  )
  const alimtalkVariableNames = useMemo(() => templateVariableNames(selectedTemplate), [selectedTemplate])
  const requiredVariableNames = useMemo(
    () => parseVariableField(selectedTemplate?.requiredVariables),
    [selectedTemplate],
  )
  const requiredVariableSet = useMemo(() => new Set(requiredVariableNames), [requiredVariableNames])
  const requiredVariablesReady = requiredVariableNames.every((name) => String(alimtalkVariables[name] || '').trim())
  const alimtalkAvailableSelectedCount = useMemo(
    () => selected.filter(canReceiveKakao).length,
    [selected],
  )

  const webAppEnabled = webEnabled || appEnabled
  const channelsSelected = webAppEnabled || alimtalkEnabled
  const targetReady = targetMode === TARGET_ACTIVE_PUSH || selected.length > 0
  const pushPayloadReady = !webAppEnabled || (title.trim() && body.trim())
  const alimtalkPayloadReady = !alimtalkEnabled || (
    targetMode === TARGET_SELECTED
      && selected.length > 0
      && alimtalkAvailableSelectedCount > 0
      && Boolean(selectedTemplateCode)
      && requiredVariablesReady
  )
  const canSend = Boolean(channelsSelected
    && targetReady
    && pushPayloadReady
    && alimtalkPayloadReady
    && (targetMode !== TARGET_ACTIVE_PUSH || confirmed)
    && !sending)

  const addRecipient = (item) => {
    if (!item || selectedIds.has(item.userId)) return
    setSelected((prev) => [...prev, item])
  }

  const addRecipients = (items) => {
    const rows = Array.isArray(items) ? items : []
    setSelected((prev) => {
      const seen = new Set(prev.map((item) => item.userId))
      const next = [...prev]
      for (const item of rows) {
        if (!item?.userId || seen.has(item.userId)) continue
        seen.add(item.userId)
        next.push(item)
      }
      return next
    })
  }

  const removeRecipient = (userId) => {
    setSelected((prev) => prev.filter((item) => item.userId !== userId))
  }

  const selectOrgRecipients = async () => {
    if (!selectedOrgId) {
      show('조직을 선택해 주세요.', 'error')
      return
    }
    setOrgSelecting(true)
    try {
      const res = await api.get('/operator/push-notifications/recipients', {
        params: { orgId: Number(selectedOrgId) },
        __silent: true,
      })
      const rows = unwrap(res, [])
      const list = Array.isArray(rows) ? rows : []
      setCandidates(list)
      addRecipients(list)
      show(`${orgLabel(selectedOrg)} 사용자를 선택 목록에 추가했습니다.`, 'success')
    } catch (error) {
      show(getApiErrorMessage(error, '조직 사용자 목록을 불러오지 못했습니다.'), 'error')
    } finally {
      setOrgSelecting(false)
    }
  }

  const toggleAlimtalk = (checked) => {
    setAlimtalkEnabled(checked)
    if (checked && targetMode === TARGET_ACTIVE_PUSH) {
      setTargetMode(TARGET_SELECTED)
      setConfirmed(false)
    }
  }

  const setVariable = (name, value) => {
    setAlimtalkVariables((prev) => ({ ...prev, [name]: value }))
  }

  const sendNotification = async (event) => {
    event.preventDefault()
    if (!canSend) {
      show('발송 값을 확인해 주세요.', 'error')
      return
    }
    setSending(true)
    setResult(null)
    try {
      const combined = {}
      let recipientCount = 0

      if (alimtalkEnabled) {
        const alimtalkPayload = {
          targetMode: TARGET_SELECTED,
          userIds: selected.map((item) => item.userId),
          confirmed: true,
          messagePurpose,
          templateCode: selectedTemplateCode,
          variables: alimtalkVariables,
        }
        const res = await api.post('/operator/alimtalk-sends/send-now', alimtalkPayload)
        const data = await unwrapAsyncResult(res)
        combined.alimtalk = data
        recipientCount = Math.max(recipientCount, Number(data?.recipientCount || 0))
      }

      if (webAppEnabled) {
        const payload = {
          targetMode,
          userIds: targetMode === TARGET_SELECTED ? selected.map((item) => item.userId) : [],
          webEnabled,
          appEnabled,
          confirmed,
          messagePurpose,
          title: title.trim(),
          body: body.trim(),
        }
        const res = await api.post('/operator/push-notifications', payload)
        const data = await unwrapAsyncResult(res)
        Object.assign(combined, data || {})
        recipientCount = Math.max(recipientCount, Number(data?.recipientCount || 0))
      }

      combined.recipientCount = recipientCount
      setResult(combined)
      show('발송 요청을 처리했습니다.', 'success')
    } catch (error) {
      show(getApiErrorMessage(error, '발송에 실패했습니다.'), 'error')
    } finally {
      setSending(false)
    }
  }

  return (
    <section className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-bold text-slate-950">앱/웹 알림</h1>
      </div>

      <form onSubmit={sendNotification} className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_360px]">
        <div className="space-y-5">
          <div className="rounded border border-slate-200 bg-white p-5">
            <div className="grid gap-4 md:grid-cols-3">
              <label className="block">
                <span className="mb-1 block text-sm font-semibold text-slate-700">제목</span>
                <input
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  maxLength={100}
                  className="w-full rounded border border-slate-300 px-3 py-2 text-sm focus:border-brand-sky focus:outline-none focus:ring-2 focus:ring-brand-sky/20"
                />
              </label>
              <div>
                <span className="mb-1 block text-sm font-semibold text-slate-700">채널</span>
                <div className="flex min-h-[38px] flex-wrap items-center gap-4 rounded border border-slate-200 px-3 py-2 text-sm">
                  <label className="inline-flex items-center gap-2">
                    <input type="checkbox" checked={webEnabled} onChange={(e) => setWebEnabled(e.target.checked)} />
                    웹 알림
                  </label>
                  <label className="inline-flex items-center gap-2">
                    <input type="checkbox" checked={appEnabled} onChange={(e) => setAppEnabled(e.target.checked)} />
                    앱 푸시
                  </label>
                  <label className="inline-flex items-center gap-2">
                    <input type="checkbox" checked={alimtalkEnabled} onChange={(e) => toggleAlimtalk(e.target.checked)} />
                    알림톡
                  </label>
                </div>
              </div>
              <label className="block">
                <span className="mb-1 block text-sm font-semibold text-slate-700">발송 목적</span>
                <select
                  value={messagePurpose}
                  onChange={(e) => setMessagePurpose(e.target.value)}
                  className="w-full rounded border border-slate-300 px-3 py-2 text-sm focus:border-brand-sky focus:outline-none focus:ring-2 focus:ring-brand-sky/20"
                >
                  <option value={PURPOSE_SERVICE_NOTICE}>서비스 안내</option>
                  <option value={PURPOSE_MARKETING}>마케팅·광고</option>
                </select>
              </label>
            </div>

            <label className="mt-4 block">
              <span className="mb-1 block text-sm font-semibold text-slate-700">본문</span>
              <textarea
                value={body}
                onChange={(e) => setBody(e.target.value)}
                maxLength={500}
                rows={5}
                className="w-full resize-y rounded border border-slate-300 px-3 py-2 text-sm focus:border-brand-sky focus:outline-none focus:ring-2 focus:ring-brand-sky/20"
              />
            </label>
          </div>

          {alimtalkEnabled ? (
            <div className="rounded border border-slate-200 bg-white p-5">
              <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_180px]">
                <label className="block">
                  <span className="mb-1 block text-sm font-semibold text-slate-700">알림톡 템플릿</span>
                  <select
                    value={selectedTemplateCode}
                    onChange={(e) => setSelectedTemplateCode(e.target.value)}
                    disabled={templatesLoading || !templates.length}
                    className="w-full rounded border border-slate-300 px-3 py-2 text-sm focus:border-brand-sky focus:outline-none focus:ring-2 focus:ring-brand-sky/20 disabled:bg-slate-50 disabled:text-slate-400"
                  >
                    <option value="">{templatesLoading ? '불러오는 중' : '템플릿 없음'}</option>
                    {templates.map((template) => (
                      <option key={template.templateCode} value={template.templateCode}>
                        {template.templateName || template.templateCode}
                      </option>
                    ))}
                  </select>
                </label>
                <div className="rounded border border-slate-200 px-3 py-2 text-sm">
                  <div className="text-xs font-semibold text-slate-500">알림톡 가능</div>
                  <div className="mt-1 font-semibold text-slate-900">
                    {alimtalkAvailableSelectedCount.toLocaleString()} / {selected.length.toLocaleString()}명
                  </div>
                </div>
              </div>

              {alimtalkVariableNames.length ? (
                <div className="mt-4 grid gap-3 md:grid-cols-2">
                  {alimtalkVariableNames.map((name) => {
                    const required = requiredVariableSet.has(name)
                    return (
                      <label key={name} className="block">
                        <span className="mb-1 block text-sm font-semibold text-slate-700">
                          {variableLabel(name)} {required ? <span className="text-red-600">*</span> : null}
                        </span>
                        <textarea
                          value={alimtalkVariables[name] || ''}
                          onChange={(e) => setVariable(name, e.target.value)}
                          maxLength={3000}
                          rows={name === 'message' || name === 'content' || name === 'summary' ? 4 : 2}
                          className="w-full resize-y rounded border border-slate-300 px-3 py-2 text-sm focus:border-brand-sky focus:outline-none focus:ring-2 focus:ring-brand-sky/20"
                        />
                      </label>
                    )
                  })}
                </div>
              ) : (
                <div className="mt-4 rounded bg-slate-50 px-3 py-3 text-sm text-slate-500">템플릿 변수가 없습니다.</div>
              )}
            </div>
          ) : null}

          <div className="rounded border border-slate-200 bg-white p-5">
            <div className="mb-4 flex flex-wrap gap-2">
              <button
                type="button"
                className={`rounded border px-3 py-2 text-sm font-semibold ${targetMode === TARGET_SELECTED ? 'border-brand-navy bg-sky-50 text-brand-navy' : 'border-slate-200 text-slate-700 hover:bg-slate-50'}`}
                onClick={() => setTargetMode(TARGET_SELECTED)}
              >
                선택 사용자
              </button>
              <button
                type="button"
                disabled={alimtalkEnabled}
                className={`rounded border px-3 py-2 text-sm font-semibold ${targetMode === TARGET_ACTIVE_PUSH ? 'border-brand-navy bg-sky-50 text-brand-navy' : 'border-slate-200 text-slate-700 hover:bg-slate-50'} ${alimtalkEnabled ? 'cursor-not-allowed border-slate-200 bg-slate-50 text-slate-400 hover:bg-slate-50' : ''}`}
                onClick={() => {
                  if (!alimtalkEnabled) setTargetMode(TARGET_ACTIVE_PUSH)
                }}
              >
                앱 토큰 보유자 전체
              </button>
            </div>

            {targetMode === TARGET_SELECTED ? (
              <div className="space-y-4">
                <div className="grid gap-3 lg:grid-cols-[minmax(220px,320px)_minmax(0,1fr)]">
                  <label className="block">
                    <span className="mb-1 block text-sm font-semibold text-slate-700">조직 검색</span>
                    <input
                      value={orgQuery}
                      onChange={(e) => setOrgQuery(e.target.value)}
                      placeholder="조직명"
                      className="w-full rounded border border-slate-300 px-3 py-2 text-sm focus:border-brand-sky focus:outline-none focus:ring-2 focus:ring-brand-sky/20"
                    />
                  </label>
                  <label className="block">
                    <span className="mb-1 block text-sm font-semibold text-slate-700">조직 선택</span>
                    <select
                      value={selectedOrgId}
                      onChange={(e) => {
                        setSelectedOrgId(e.target.value)
                        setQuery('')
                      }}
                      className="w-full rounded border border-slate-300 px-3 py-2 text-sm focus:border-brand-sky focus:outline-none focus:ring-2 focus:ring-brand-sky/20"
                    >
                      <option value="">{orgsLoading ? '조직 불러오는 중' : '전체 조직'}</option>
                      {filteredOrgOptions.map((org) => (
                        <option key={org.id} value={org.id}>{orgLabel(org)}</option>
                      ))}
                    </select>
                  </label>
                </div>

                <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(280px,360px)]">
                  <div>
                    <div className="flex flex-wrap items-end gap-2">
                      <label className="relative block min-w-[220px] flex-1">
                        <span className="mb-1 block text-sm font-semibold text-slate-700">사용자 목록</span>
                        <Search className="pointer-events-none absolute left-3 top-[34px] h-4 w-4 text-slate-400" />
                        <input
                          type="search"
                          value={query}
                          onChange={(e) => setQuery(e.target.value)}
                          placeholder="이름 또는 이메일"
                          className="w-full rounded border border-slate-300 py-2 pl-9 pr-3 text-sm focus:border-brand-sky focus:outline-none focus:ring-2 focus:ring-brand-sky/20"
                        />
                      </label>
                      <button
                        type="button"
                        onClick={selectOrgRecipients}
                        disabled={!selectedOrgId || orgSelecting}
                        className="rounded border border-brand-navy px-3 py-2 text-sm font-semibold text-brand-navy hover:bg-sky-50 disabled:border-slate-200 disabled:text-slate-400 disabled:hover:bg-transparent"
                      >
                        {orgSelecting ? '선택 중' : '조직 전체 선택'}
                      </button>
                    </div>
                    <div className="mt-3 max-h-[360px] overflow-auto rounded border border-slate-200">
                      {loading ? (
                        <div className="px-3 py-6 text-center text-sm text-slate-500">검색 중</div>
                      ) : availableCandidates.length ? (
                        availableCandidates.map((item) => (
                          <button
                            key={item.userId}
                            type="button"
                            onClick={() => addRecipient(item)}
                            className="flex w-full items-center justify-between gap-3 border-b border-slate-100 px-3 py-2 text-left text-sm last:border-b-0 hover:bg-slate-50"
                          >
                            <span className="min-w-0">
                              <span className="block truncate font-semibold text-slate-900">{item.name || item.email}</span>
                              <span className="block truncate text-xs text-slate-500">{item.email} · {item.orgName || '-'}</span>
                            </span>
                            <span className="flex shrink-0 flex-wrap justify-end gap-1">
                              <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${item.hasActivePushToken ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
                                앱 {Number(item.activePushTokenCount || 0)}
                              </span>
                              <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${canReceiveKakao(item) ? 'bg-amber-50 text-amber-700' : 'bg-slate-100 text-slate-500'}`}>
                                {canReceiveKakao(item) ? '알림톡' : '알림톡 불가'}
                              </span>
                            </span>
                          </button>
                        ))
                      ) : (
                        <div className="px-3 py-6 text-center text-sm text-slate-500">검색 결과가 없습니다.</div>
                      )}
                    </div>
                  </div>

                  <div className="rounded border border-slate-200">
                    <div className="border-b border-slate-200 px-3 py-2 text-sm font-semibold text-slate-800">
                      선택 {selected.length.toLocaleString()}명
                    </div>
                    <div className="max-h-[360px] overflow-auto">
                      {selected.length ? selected.map((item) => (
                        <div key={item.userId} className="flex items-center gap-2 border-b border-slate-100 px-3 py-2 text-sm last:border-b-0">
                          <div className="min-w-0 flex-1">
                            <div className="truncate font-semibold text-slate-900">{item.name || item.email}</div>
                            <div className="truncate text-xs text-slate-500">
                              {item.orgName || '-'} · 앱 토큰 {Number(item.activePushTokenCount || 0).toLocaleString()}개 · {canReceiveKakao(item) ? '알림톡 가능' : '알림톡 불가'}
                            </div>
                          </div>
                          <button
                            type="button"
                            className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
                            aria-label="대상 제거"
                            onClick={() => removeRecipient(item.userId)}
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      )) : (
                        <div className="px-3 py-6 text-center text-sm text-slate-500">선택된 사용자가 없습니다.</div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <label className="flex items-start gap-2 rounded border border-amber-200 bg-amber-50 px-3 py-3 text-sm text-amber-900">
                <input
                  type="checkbox"
                  checked={confirmed}
                  onChange={(e) => setConfirmed(e.target.checked)}
                  className="mt-0.5"
                />
                <span>앱 토큰이 활성화된 모든 사용자에게 발송합니다.</span>
              </label>
            )}
          </div>
        </div>

        <aside className="space-y-4">
          <div className="rounded border border-slate-200 bg-white p-4">
            <button
              type="submit"
              disabled={!canSend}
              className="inline-flex w-full items-center justify-center gap-2 rounded bg-brand-navy px-4 py-2.5 text-sm font-semibold text-white hover:bg-brand-navy/90 disabled:opacity-50"
            >
              <Send className="h-4 w-4" />
              {sending ? '발송 중' : '발송'}
            </button>
          </div>

          {result ? (
            <div className="rounded border border-slate-200 bg-white p-4 text-sm">
              <div className="font-semibold text-slate-900">발송 결과</div>
              <dl className="mt-3 space-y-2">
                <div className="flex justify-between gap-3">
                  <dt className="text-slate-500">대상</dt>
                  <dd className="font-semibold text-slate-900">{Number(result.recipientCount || 0).toLocaleString()}명</dd>
                </div>
                {result.web ? (
                  <div className="flex justify-between gap-3">
                    <dt className="text-slate-500">웹</dt>
                    <dd className="text-right font-semibold text-slate-900">
                      {Number(result.web.attemptedUsers || 0).toLocaleString()}명 · 연결 {Number(result.web.connectedEmitters || 0).toLocaleString()}
                    </dd>
                  </div>
                ) : null}
                {result.app ? (
                  <>
                    <div className="flex justify-between gap-3">
                      <dt className="text-slate-500">앱 토큰</dt>
                      <dd className="font-semibold text-slate-900">{Number(result.app.tokenCount || 0).toLocaleString()}개</dd>
                    </div>
                    <div className="flex justify-between gap-3">
                      <dt className="text-slate-500">앱 푸시</dt>
                      <dd className="text-right font-semibold text-slate-900">{resultLabel(result.app)}</dd>
                    </div>
                  </>
                ) : null}
                {result.alimtalk ? (
                  <>
                    <div className="flex justify-between gap-3">
                      <dt className="text-slate-500">알림톡 템플릿</dt>
                      <dd className="text-right font-semibold text-slate-900">{result.alimtalk.templateName || result.alimtalk.templateCode}</dd>
                    </div>
                    <div className="flex justify-between gap-3">
                      <dt className="text-slate-500">알림톡</dt>
                      <dd className="text-right font-semibold text-slate-900">{alimtalkResultLabel(result.alimtalk)}</dd>
                    </div>
                  </>
                ) : null}
              </dl>
              {Array.isArray(result.alimtalk?.failureSamples) && result.alimtalk.failureSamples.length ? (
                <div className="mt-3 max-h-36 overflow-auto rounded bg-amber-50 p-2 text-xs text-amber-900">
                  {result.alimtalk.failureSamples.map((sample, idx) => (
                    <div key={`${sample.userId || 'unknown'}-${idx}`}>
                      {sample.name || sample.userId || '-'} · {sample.phoneMasked || '-'} · {sample.errorCode || 'UNKNOWN'}
                    </div>
                  ))}
                </div>
              ) : null}
              {Array.isArray(result.app?.chunks) && result.app.chunks.length ? (
                <div className="mt-3 max-h-40 overflow-auto rounded bg-slate-50 p-2 font-mono text-[11px] text-slate-600">
                  {result.app.chunks.map((chunk, idx) => (
                    <div key={idx}>
                      #{idx + 1} {chunk.success ? 'OK' : 'FAIL'} {chunk.errorCode || ''} {chunk.responseBody || ''}
                    </div>
                  ))}
                </div>
              ) : null}
            </div>
          ) : null}
        </aside>
      </form>
    </section>
  )
}
